import { CallStatus, CallStatusDefault } from '../models';
import { MessageType } from '../core/localization';

export const fetch = <T extends CallStatus>(item: T) =>
  ({
    ...item,
    isFetching: true,
    errorMsg: '',
  } as T);

export const error = <T extends CallStatus>(
  item: T,
  errorMsg: MessageType
): T =>
  ({
    ...item,
    isFetching: false,
    errorMsg,
  } as T);

export const success = <T extends CallStatus>(item: T): T => error(item, '');

export const reset = <T extends CallStatus>(item: T): T => ({
  ...item,
  ...CallStatusDefault,
});

export const override = <T extends CallStatus>(base: T, item: T) => {
  base.isFetching = item.isFetching;
  base.errorMsg = item.errorMsg;
};

export const hasError = (status: CallStatus): boolean =>
  (status.errorMsg?.toString().length || 0) > 0;

/******************************************* */

interface IHelperActions<T, S extends ActionType<T,S>> {}
interface IHelperEvent<T> extends IHelperActions<T, void> {}
interface IHelperAction<T, S>extends IHelperActions<IHelperResult<T, S>, void> {};
interface IHelperResult<T, S> extends IHelperAction<T, IHelperResult<T, S>> {
  data: T;
  self: IHelperEvent<T>
}
type ActionType<T, S> = IHelperResult<T, S> | void;

type CallActionType1<T> = ActionType<T, ICallHelperResult1<T>>;
interface ICallStatusActions1<T, R extends CallActionType1<T>> {
  reset: () => R;
  fetch: () => R;
  success: () => R;
  error: (m: MessageType) => R;
}

interface ICallHelperResult1<T> extends ICallStatusActions1<T, ICallHelperResult1<T>> {
  data: T;
  self: ICallStatusActions1<T, void>
}

type ICallHelper1<T> = (data?: T) => ICallHelperResult1<T>;
type CallHelper1 = ICallHelper1<CallStatus>;
export type CallHelperResult = ICallHelperResult1<CallStatus>;
const helperCall: CallHelper1 = (call = CallStatusDefault)  => {
  const d = { ...call };

  return {
    data: d,
    self: {
      reset: () => override(d, reset(d)),
      fetch: () => override(d, fetch(d)),
      success: () => override(d, success(d)),
      error: (m) => override(d, error(d, m)),
    },
    reset: () => helperCall(reset(d)),
    fetch: () => helperCall(fetch(d)),
    success: () => helperCall(success(d)),
    error: (m) => helperCall(error(d, m)),
  };
};

export default helperCall;
